import React from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// TimePicker12 — a 12-hour AM/PM time field.
//
// Replaces <input type="time">, whose 12h-vs-24h display is chosen by the
// viewer's OS locale and cannot be forced from markup: the same build showed
// "19:04" to a UK-locale user and "7:04 PM" to a US one. Interview times are
// read aloud to candidates, so they must look the same for everyone.
//
// The stored VALUE is unchanged — still 24-hour "HH:MM", exactly what
// <input type="time"> emitted — so rounds, the API, and iCalendar are all
// untouched. Only the way it is picked and displayed differs.
//
// Native <select>s (rather than a bespoke dropdown) keep the mobile wheel,
// keyboard navigation, and screen-reader behaviour we'd otherwise have to
// rebuild by hand.
//
//   value    — "HH:MM" 24-hour, or '' when unset
//   onChange — called with the new "HH:MM" string, or '' when cleared
// ─────────────────────────────────────────────────────────────────────────────

const HOURS   = Array.from({ length: 12 }, (_, i) => i + 1);            // 1…12
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

// "19:04" → { h12: 7, min: '04', mer: 'PM' }; null when unset or malformed.
const parse24 = (value) => {
  const m = String(value || '').match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const [h24, mi] = [Number(m[1]), Number(m[2])];
  if (h24 > 23 || mi > 59) return null;
  return { h12: h24 % 12 || 12, min: m[2], mer: h24 < 12 ? 'AM' : 'PM' };
};

// { h12: 7, min: '04', mer: 'PM' } → "19:04"
const to24 = ({ h12, min, mer }) => {
  const h24 = mer === 'AM' ? (h12 === 12 ? 0 : h12) : (h12 === 12 ? 12 : h12 + 12);
  return `${String(h24).padStart(2, '0')}:${min}`;
};

const SELECT_CLASS = 'input-premium rounded-lg px-2 py-2.5 text-sm focus:outline-none';
const SELECT_STYLE = { background: 'var(--input-bg)', color: 'var(--text-primary)' };

export default function TimePicker12({ label, value = '', onChange, className = '' }) {
  const cur = parse24(value);

  // Minute and meridiem are meaningless without an hour, so they stay disabled
  // until one is picked — that also removes the dead interaction where changing
  // them on an empty field could not produce a time.
  const update = (patch) => {
    const next = { h12: cur?.h12, min: cur?.min ?? '00', mer: cur?.mer ?? 'AM', ...patch };
    if (!next.h12) return onChange('');
    onChange(to24(next));
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-secondary)' }}>
          {label}
        </label>
      )}
      <div className="flex items-center gap-1">
        <select
          aria-label={`${label || 'Time'} — hour`}
          className={`${SELECT_CLASS} flex-1`}
          style={SELECT_STYLE}
          value={cur?.h12 ?? ''}
          onChange={e => update({ h12: e.target.value ? Number(e.target.value) : undefined })}
        >
          <option value="">--</option>
          {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
        </select>

        <span style={{ color: 'var(--text-muted)' }}>:</span>

        <select
          aria-label={`${label || 'Time'} — minute`}
          className={`${SELECT_CLASS} flex-1`}
          style={SELECT_STYLE}
          value={cur?.min ?? ''}
          disabled={!cur}
          onChange={e => update({ min: e.target.value })}
        >
          {!cur && <option value="">--</option>}
          {MINUTES.map(mi => <option key={mi} value={mi}>{mi}</option>)}
        </select>

        <select
          aria-label={`${label || 'Time'} — AM or PM`}
          className={`${SELECT_CLASS} flex-1`}
          style={SELECT_STYLE}
          value={cur?.mer ?? ''}
          disabled={!cur}
          onChange={e => update({ mer: e.target.value })}
        >
          {!cur && <option value="">--</option>}
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
}
